import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Alert,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Grid,
  Container,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Tabs,
  Tab,
  IconButton,
  Tooltip
} from '@mui/material';
import axios from 'axios';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import TuneIcon from '@mui/icons-material/Tune';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { useSnackbar } from 'notistack';

const API_URL = process.env.REACT_APP_API_URL;

const getScoreColor = (pct) => {
  if (pct >= 80) return '#48BB78';
  if (pct >= 60) return '#4299E1';
  if (pct >= 40) return '#ECC94B';
  return '#F56565';
};

const STYLE_OPTIONS = [
  { value: 'professional', label: 'Professional' },
  { value: 'creative', label: 'Creative' },
  { value: 'technical', label: 'Technical' },
  { value: 'executive', label: 'Executive' },
  { value: 'conversational', label: 'Conversational' },
];

const DEFAULT_FORM = {
  role: '',
  experience: '',
  skills: '',
  achievements: '',
  industry: '',
  targetAudience: '',
  style: 'professional',
};

const AboutSection = () => {
  const { enqueueSnackbar } = useSnackbar();

  // 0 = Generate from form, 1 = Optimize existing
  const [tab, setTab] = useState(0);

  // Generate from form
  const [form, setForm] = useState(DEFAULT_FORM);
  const [generating, setGenerating] = useState(false);

  // Optimize existing
  const [currentAbout, setCurrentAbout] = useState('');
  const [optimizeStyle, setOptimizeStyle] = useState('professional');
  const [optimizing, setOptimizing] = useState(false);

  // Shared output + analysis
  const [generatedText, setGeneratedText] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analysisError, setAnalysisError] = useState('');

  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const runAnalysis = async (text) => {
    setAnalysis(null);
    setAnalysisError('');
    setAnalyzing(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/linkedin/analyze-about`,
        { aboutText: text },
        { headers: authHeader() }
      );
      if (res.data) {
        setAnalysis(res.data);
        // persist to db
        const total = res.data.category_scores
          ? Object.values(res.data.category_scores).reduce((s, v) => s + v, 0)
          : res.data.score;
        await axios.post(
          `${API_URL}/api/linkedin/about-analysis`,
          {
            aboutText: text,
            analysisScore: total || 0,
            analysisText: res.data.analysis || '',
            categoryScores: res.data.category_scores || {},
            strengths: res.data.strengths || [],
            recommendations: res.data.recommendations || [],
          },
          { headers: authHeader() }
        ).catch(() => {});
      }
    } catch (err) {
      setAnalysisError(err.response?.data?.message || 'Failed to analyse about section.');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setGeneratedText('');
    setAnalysis(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/linkedin/generate-about`,
        { ...form },
        { headers: authHeader() }
      );
      const text = res.data?.about_text || '';
      if (!text) throw new Error('Empty response');
      setGeneratedText(text);
      enqueueSnackbar('About section generated!', { variant: 'success' });
      await runAnalysis(text);
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Generation failed. Please try again.', { variant: 'error' });
    } finally {
      setGenerating(false);
    }
  };

  const handleOptimize = async () => {
    if (!currentAbout.trim()) {
      enqueueSnackbar('Please paste your current about section first.', { variant: 'warning' });
      return;
    }
    setOptimizing(true);
    setGeneratedText('');
    setAnalysis(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/linkedin/generate-about`,
        {
          currentAbout: currentAbout.trim(),
          style: optimizeStyle,
          optimizeMode: true,
        },
        { headers: authHeader() }
      );
      const text = res.data?.about_text || '';
      if (!text) throw new Error('Empty response');
      setGeneratedText(text);
      enqueueSnackbar('Optimized version ready!', { variant: 'success' });
      await runAnalysis(text);
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Optimization failed. Please try again.', { variant: 'error' });
    } finally {
      setOptimizing(false);
    }
  };

  const copyText = () => {
    navigator.clipboard.writeText(generatedText);
    enqueueSnackbar('Copied to clipboard!', { variant: 'info' });
  };

  const isFormValid = form.role && form.experience && form.skills;

  return (
    <>
      <Helmet>
        <title>About Section Optimizer | Instation</title>
        <meta name="description" content="Generate or optimize your LinkedIn About section with AI." />
      </Helmet>

      <Container maxWidth="lg" className="page-container">
        <Box className="content-container">
          <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid rgba(0,0,0,0.08)', mb: 3 }}>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <AutoAwesomeIcon sx={{ color: '#9c27b0', mr: 1.5, fontSize: 28 }} />
              <Box>
                <Typography variant="h6" fontWeight={700}>About Section Optimizer</Typography>
                <Typography variant="body2" color="text.secondary">
                  Generate a new About section or paste your current one and get an AI-optimized version.
                </Typography>
              </Box>
            </Box>

            <Tabs
              value={tab}
              onChange={(_, v) => { setTab(v); setGeneratedText(''); setAnalysis(null); setAnalysisError(''); }}
              sx={{ mb: 3, borderBottom: '1px solid rgba(0,0,0,0.08)' }}
            >
              <Tab icon={<TuneIcon fontSize="small" />} iconPosition="start" label="Generate from scratch" />
              <Tab icon={<EditNoteIcon fontSize="small" />} iconPosition="start" label="Optimize existing" />
            </Tabs>

            {/* ── TAB 0 — Generate from form ── */}
            {tab === 0 && (
              <Stack spacing={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Professional Role *"
                      fullWidth
                      placeholder="e.g. Product Manager, Full-Stack Developer"
                      value={form.role}
                      onChange={e => setForm(p => ({ ...p, role: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Industry *"
                      fullWidth
                      placeholder="e.g. Technology, Healthcare, Finance"
                      value={form.industry}
                      onChange={e => setForm(p => ({ ...p, industry: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Experience *"
                      fullWidth
                      multiline rows={2}
                      placeholder="e.g. 7 years building SaaS products at early-stage startups"
                      value={form.experience}
                      onChange={e => setForm(p => ({ ...p, experience: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Key Skills *"
                      fullWidth
                      multiline rows={2}
                      placeholder="e.g. React, Node.js, Product Strategy, Team Leadership"
                      value={form.skills}
                      onChange={e => setForm(p => ({ ...p, skills: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Notable Achievements"
                      fullWidth
                      multiline rows={2}
                      placeholder="e.g. Grew ARR from $0 to $2M, Led team of 12, Launched app with 50k downloads"
                      value={form.achievements}
                      onChange={e => setForm(p => ({ ...p, achievements: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Target Audience"
                      fullWidth
                      placeholder="e.g. Tech recruiters, Enterprise clients, Founders"
                      value={form.targetAudience}
                      onChange={e => setForm(p => ({ ...p, targetAudience: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Writing Style</InputLabel>
                      <Select
                        value={form.style}
                        label="Writing Style"
                        onChange={e => setForm(p => ({ ...p, style: e.target.value }))}
                      >
                        {STYLE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    onClick={handleGenerate}
                    disabled={generating || !isFormValid}
                    startIcon={generating ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
                    sx={{ bgcolor: '#9c27b0', '&:hover': { bgcolor: '#7b1fa2' }, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                  >
                    {generating ? 'Generating…' : 'Generate About Section'}
                  </Button>
                </Box>
              </Stack>
            )}

            {/* ── TAB 1 — Optimize existing ── */}
            {tab === 1 && (
              <Stack spacing={2}>
                <TextField
                  label="Your Current About Section *"
                  fullWidth
                  multiline rows={7}
                  placeholder="Paste your existing LinkedIn About section here…"
                  value={currentAbout}
                  onChange={e => setCurrentAbout(e.target.value)}
                />

                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Target Style</InputLabel>
                      <Select
                        value={optimizeStyle}
                        label="Target Style"
                        onChange={e => setOptimizeStyle(e.target.value)}
                      >
                        {STYLE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                    <Button
                      variant="contained"
                      onClick={handleOptimize}
                      disabled={optimizing || !currentAbout.trim()}
                      startIcon={optimizing ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
                      sx={{ bgcolor: '#9c27b0', '&:hover': { bgcolor: '#7b1fa2' }, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                    >
                      {optimizing ? 'Optimizing…' : 'Optimize My About Section'}
                    </Button>
                  </Grid>
                </Grid>
              </Stack>
            )}
          </Paper>

          {/* ── Output Card ── */}
          {generatedText && (
            <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid rgba(156,39,176,0.25)', mb: 3, background: 'rgba(156,39,176,0.03)' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#9c27b0' }}>
                  ✨ {tab === 0 ? 'Generated' : 'Optimized'} About Section
                </Typography>
                <Tooltip title="Copy to clipboard">
                  <IconButton size="small" onClick={copyText}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}>
                {generatedText}
              </Typography>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={copyText}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  Copy Text
                </Button>
              </Box>
            </Paper>
          )}

          {/* ── Analysis Card ── */}
          {(analyzing || analysis || analysisError) && generatedText && (
            <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid rgba(0,0,0,0.08)' }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                About Section Analysis
              </Typography>

              {analyzing && (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>Analysing…</Typography>
                  <LinearProgress sx={{ '& .MuiLinearProgress-bar': { bgcolor: '#9c27b0' } }} />
                </Box>
              )}

              {analysisError && <Alert severity="error">{analysisError}</Alert>}

              {analysis && !analyzing && (
                <Box>
                  {/* Score headline */}
                  {(analysis.score !== undefined || analysis.category_scores) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Typography variant="h6" fontWeight={600}>
                        Score:&nbsp;
                        {analysis.category_scores
                          ? `${Object.values(analysis.category_scores).reduce((s, v) => s + v, 0)} / ${Object.keys(analysis.category_scores).length * 10}`
                          : `${analysis.score}%`}
                      </Typography>
                      {analysis.score !== undefined && (
                        <Box sx={{ ml: 2, px: 2, py: 0.4, borderRadius: 10, bgcolor: `${getScoreColor(analysis.score)}20`, color: getScoreColor(analysis.score), fontWeight: 700, fontSize: 14 }}>
                          {analysis.score}%
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Category breakdown */}
                  {analysis.category_scores && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>Score Breakdown</Typography>
                      <Grid container spacing={2}>
                        {Object.entries(analysis.category_scores).map(([cat, score]) => (
                          <Grid item xs={12} sm={6} key={cat}>
                            <Box sx={{ mb: 0.5 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                                <Typography variant="body2">
                                  {cat.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>{score}/10</Typography>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={score * 10}
                                sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(0,0,0,0.06)', '& .MuiLinearProgress-bar': { bgcolor: getScoreColor(score * 10) } }}
                              />
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}

                  {analysis.analysis && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>Analysis</Typography>
                      <Typography variant="body2" color="text.secondary">{analysis.analysis}</Typography>
                    </Box>
                  )}

                  {analysis.strengths?.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>Strengths</Typography>
                      <List dense disablePadding>
                        {analysis.strengths.map((s, i) => (
                          <ListItem key={i} sx={{ py: 0.25, pl: 0 }}>
                            <ListItemText primary={`✓ ${s}`} primaryTypographyProps={{ variant: 'body2' }} />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {analysis.recommendations?.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>Recommendations</Typography>
                      <List dense disablePadding>
                        {analysis.recommendations.map((r, i) => (
                          <ListItem key={i} sx={{ py: 0.25, pl: 0 }}>
                            <ListItemText primary={`→ ${r}`} primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </Box>
              )}
            </Paper>
          )}
        </Box>
      </Container>
    </>
  );
};

export default AboutSection;
